import { useEffect, useRef, useState } from 'react';

import type { PenRef, ProviderGroup } from '@/domain/providerConfig';
import { penKey } from '@/routes/shared/ProviderConfig/form/formKeys';

import type { PenFormValues } from '../form/formModel';

export interface TestPens {
    /** The connected pens a test can cover. */
    candidates: PenRef[];
    /** The pens the test covers: the ones ticked, else the first candidate. */
    selected: number[];
    onSelectedChange: (cageIds: number[]) => void;
}

/**
 * Which pens "Pens to test" ticks. A pen connected here is ticked as it is connected, so the
 * test judges its required fields at once (R5-1); Undo connect takes it out with the pen.
 */
export function useTestPens(
    pens: PenRef[],
    penValues: Readonly<Record<string, PenFormValues>>,
    group: ProviderGroup | null
): TestPens {
    const [chosen, setChosen] = useState<number[]>([]);
    const candidates = pens.filter((pen) => {
        const values = penValues[penKey(pen.cageId)];
        return values?.connected === true && !values.remove;
    });
    const isCandidate = (cageId: number): boolean =>
        candidates.some((pen) => pen.cageId === cageId);
    const stored = new Set((group?.rows ?? []).map((row) => row.cageId));
    const connecting = candidates.map((pen) => pen.cageId).filter((id) => !stored.has(id));
    const connectingKey = connecting.join(',');
    // What "nothing ticked" meant before the new pen came: the first pen already stored.
    const firstStored = candidates.find((pen) => stored.has(pen.cageId))?.cageId ?? null;
    const seen = useRef<ReadonlySet<number>>(new Set(connecting));

    useEffect(() => {
        const now = connectingKey === '' ? [] : connectingKey.split(',').map(Number);
        const added = now.filter((cageId) => !seen.current.has(cageId));
        seen.current = new Set(now);
        if (added.length === 0) return;
        setChosen((previous) => {
            const kept = previous.filter((cageId) => !added.includes(cageId));
            // Nothing ticked meant the first pen, which stays ticked beside the new one.
            const fallback = firstStored === null ? [] : [firstStored];
            return [...(kept.length > 0 ? kept : fallback), ...added];
        });
    }, [connectingKey, firstStored]);

    const ticked = chosen.filter(isCandidate);
    const selected = ticked.length > 0 ? ticked : candidates.slice(0, 1).map((pen) => pen.cageId);
    return { candidates, selected, onSelectedChange: setChosen };
}
